import Decimal from 'decimal.js';
import { serviceModel, serviceCostModel, serviceBillingModel } from "../../models";
export let BillingGroupTypes = /*#__PURE__*/function (BillingGroupTypes) {
  BillingGroupTypes["Today"] = "today";
  BillingGroupTypes["OnEffectiveDate"] = "on the effective date";
  BillingGroupTypes["InStages"] = "in stages";
  BillingGroupTypes["Once"] = "once";
  BillingGroupTypes["Weekly"] = "weekly";
  BillingGroupTypes["BiWeekly"] = "every two weeks";
  BillingGroupTypes["Monthly"] = "monthly";
  BillingGroupTypes["Quarterly"] = "quarterly";
  BillingGroupTypes["Yearly"] = "yearly";
  return BillingGroupTypes;
}({});
export function summarize() {
  let {
    selectedOptionalServices = [],
    effectiveDate = null,
    now = new Date(),
    billingFrequencyOnly = false,
    START_BILLING_AT = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    selectedOptionalServices: [],
    effectiveDate: null,
    now: new Date(),
    billingFrequencyOnly: false,
    START_BILLING_AT: false
  };
  function calculateEffectiveStartDate(service, isAutomatic, skipsOnAcceptance) {
    if (!START_BILLING_AT) return undefined;
    const serviceStartBillingAt = serviceModel.startBillingAt(service);
    const isAgreementEffectiveDateInFuture = effectiveDate != null && effectiveDate > now;

    // priority 1: service's own startBillingAt (only if in the future)
    if (serviceStartBillingAt && serviceStartBillingAt > now) {
      return serviceStartBillingAt;
    }

    // priority 2: agreement effective date (if service is automatic, doesn't skip, and date is in future)
    if (isAutomatic && !skipsOnAcceptance && isAgreementEffectiveDateInFuture) {
      return effectiveDate;
    }

    // no start date found
    return undefined;
  }
  function getPeriodGroupType(period) {
    if (period === 'weekly') return BillingGroupTypes.Weekly;
    if (period === 'biweekly') return BillingGroupTypes.BiWeekly;
    if (period === 'monthly') return BillingGroupTypes.Monthly;
    if (period === 'quarterly') return BillingGroupTypes.Quarterly;
    return BillingGroupTypes.Yearly;
  }
  return function doSummary(summary, service) {
    const cost = serviceModel.cost(service);
    const variablePrice = serviceCostModel.isVariablePrice(cost);
    const rangePrice = serviceCostModel.isRangePrice(cost);
    const name = serviceModel.name(service);
    const billedUpfront = serviceModel.isBilledUpfront(service);
    const billing = serviceModel.billingTrigger(service);
    const period = serviceBillingModel.period(billing);
    const recurring = serviceBillingModel.isRecurring(billing);
    const automatic = serviceBillingModel.isAutomatic(billing);
    const price = serviceModel.applyDiscount(service);
    const minPrice = serviceModel.minPriceFinal(service);
    const prorated = serviceModel.isProrated(service);
    const paused = serviceModel.isPaused(service);
    const skipsOnAcceptance = serviceModel.isSkipBillingOnAcceptance(service);
    const optional = serviceModel.isOptional(service.participation) && !selectedOptionalServices.includes(serviceModel.id(service));
    const unitName = serviceModel.unitName(service);
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(billing);

    // determine which group to use for immediate billing (today or on effective date)
    const isAgreementEffectiveDateInFuture = effectiveDate != null && effectiveDate > now;
    const immediateGroupType = START_BILLING_AT ? BillingGroupTypes.Today // with START_BILLING_AT, never use OnEffectiveDate
    : isAgreementEffectiveDateInFuture ? BillingGroupTypes.OnEffectiveDate : BillingGroupTypes.Today;

    // skip services that shouldn't appear in billing schedule
    if (optional) return summary;
    if (!automatic && !oneTimeInStages && START_BILLING_AT) return summary; // manual services hidden (except in-stages)

    // handle recurring services
    if (recurring) {
      const serviceStartDate = calculateEffectiveStartDate(service, automatic, skipsOnAcceptance);
      const shouldBillImmediately = automatic && billedUpfront && !paused && !skipsOnAcceptance && !serviceStartDate;
      if (shouldBillImmediately) {
        addServiceToSummary(immediateGroupType);
      }
    } else {
      if (oneTimeInStages) {
        oneTimeInStages.stagesWithFinalAmount(cost).forEach((stage, index) => {
          const isFirstStageAutomatic = index === 0 && oneTimeInStages.isAutoFirst();
          // when START_BILLING_AT is false, first stage always goes to immediate group
          // when START_BILLING_AT is true, only if effective date is NOT in future
          const shouldSkipImmediate = START_BILLING_AT && isAgreementEffectiveDateInFuture;
          const firstStageBillsImmediately = isFirstStageAutomatic && !billingFrequencyOnly && !shouldSkipImmediate;
          const groupType = firstStageBillsImmediately ? immediateGroupType : BillingGroupTypes.InStages;
          addBillingStageToSummary(groupType, oneTimeInStages, stage, cost);
        });
      } else if (automatic && !billingFrequencyOnly) {
        addServiceToSummary(immediateGroupType);
      } else {
        addServiceToSummary(BillingGroupTypes.Once);
      }
    }
    if (period != null) {
      const periodGroup = getPeriodGroupType(period);
      addServiceToSummary(periodGroup);
    }
    return summary;
    function addBillingStageToSummary(groupType, oneTimeInStages, billingStage, cost) {
      const group = getServiceSummary(groupType);
      let effectiveStartDate = undefined;
      if (START_BILLING_AT) {
        const isFirstStageAutomatic = billingStage.stageNumber === 1 && oneTimeInStages.isAutoFirst();
        const isAgreementEffectiveDateInFuture = effectiveDate != null && effectiveDate > now;
        if (isFirstStageAutomatic && isAgreementEffectiveDateInFuture) {
          effectiveStartDate = effectiveDate || undefined;
        }
      }
      group.totalAmount = group.totalAmount.plus(billingStage.discountedFinalAmount);
      group.services.push({
        name,
        price: billingStage.discountedFinalAmount,
        discount: cost.discount,
        minPrice: null,
        prorated: false,
        isRange: false,
        unit: null,
        effectiveStartDate,
        isPaused: paused,
        billingStages: {
          number: billingStage.stageNumber,
          total: oneTimeInStages.numStages()
        }
      });
    }
    function addServiceToSummary(groupType) {
      const group = getServiceSummary(groupType);
      const effectiveStartDate = calculateEffectiveStartDate(service, automatic, skipsOnAcceptance);
      group.totalAmount = group.totalAmount.plus(price);
      group.services = group.services.concat({
        name,
        price,
        minPrice,
        prorated,
        isRange: rangePrice,
        unit: unitName,
        discount: cost.discount,
        effectiveStartDate,
        isPaused: paused
      });
      group.hasBilledUpfront = group.hasBilledUpfront || billedUpfront || false;
      if (effectiveStartDate) {
        group.startDate = effectiveStartDate;
      }
      return summary;
    }
    function getServiceSummary(groupType) {
      if (summary[groupType] == null) summary[groupType] = new Map();
      if (!summary[groupType].has(variablePrice || rangePrice)) summary[groupType].set(variablePrice || rangePrice, {
        totalAmount: new Decimal(0),
        services: [],
        hasBilledUpfront: false,
        startDate: undefined
      });
      return summary[groupType].get(variablePrice || rangePrice);
    }
  };
}